import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(ScrollTrigger);
const defaultScreen = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px"
};
let matchMedia;
function getVariables(element, dataKey) {
  const data = element.dataset[dataKey];
  if (data)
    return JSON.parse(data);
}
function getMediaQuery(screen, type, screens) {
  if (!screens)
    screens = defaultScreen;
  if (!screen) {
    if (type === "min")
      return "(min-width: 0px)";
    if (type === "max")
      throw new Error('Only the type "min" is allowed if screen is undefined.');
    return void 0;
  }
  if (screen && !screens[screen]) {
    throw new Error(
      `Breakpoint "${screen}" not found in screens (${Object.keys(screens).join(
        ", "
      )}).`
    );
  }
  return type === "min" ? `(min-width: ${screens[screen]})` : `(max-width: ${Number.parseInt(screens[screen]) - 1}px)`;
}
function warn(message) {
  console.warn(`[data-scroll] ${message}`);
}
function handleError(error) {
  if (error instanceof Error) {
    warn(error.message);
  } else if (typeof error === "string") {
    warn(error);
  }
}
function apply(target, options) {
  const {
    getSpeed = () => target.dataset.scrollSpeed,
    getFrom = () => getVariables(target, "scrollFrom"),
    getTo = () => getVariables(target, "scrollTo"),
    getUseMarkers = () => Object.hasOwn(target.dataset, "scrollMarkers")
  } = options || {};
  const dataSpeed = getSpeed(target) ?? "1";
  const dataFrom = getFrom(target);
  const dataTo = getTo(target);
  const markers = getUseMarkers(target);
  const values = dataSpeed.split(" ");
  for (const [index, value] of values.entries()) {
    const conditions = [];
    const screen = value.includes(":") ? value.split(":")[0] : void 0;
    try {
      const mediaQuery = getMediaQuery(screen, "min", options?.screens);
      conditions.push(mediaQuery);
    } catch (error) {
      handleError(error);
      continue;
    }
    const nextValue = index < values.length - 1 ? values[index + 1] : void 0;
    if (nextValue) {
      const nextScreen = nextValue.includes(":") ? nextValue.split(":")[0] : void 0;
      if (!nextScreen) {
        warn(
          `Only the first value can omit the screen (${nextScreen} in "${dataSpeed}").`
        );
        continue;
      }
      try {
        const mediaQuery = getMediaQuery(nextScreen, "max", options?.screens);
        conditions.push(mediaQuery);
      } catch (error) {
        handleError(error);
        continue;
      }
    }
    matchMedia.add(conditions.join(" and "), () => {
      const rawSpeed = value.includes(":") ? value.split(":")[1] : value;
      const clamp = rawSpeed.startsWith("clamp(");
      const speed = Number.parseFloat(clamp ? rawSpeed.slice(6, -1) : rawSpeed);
      if (Number.isNaN(speed))
        return;
      if (speed === 1 && !dataFrom && !dataTo)
        return;
      if (speed <= 0) {
        warn(`Speed must be greater than 0 (${speed} in "${dataSpeed}").`);
        return;
      }
      let endOffset = 0;
      let startOffset = 0;
      const updateOffsets = (scrollTrigger) => {
        const viewportHeight = window.innerHeight;
        const targetHeight = target.offsetHeight;
        if (clamp) {
          target.style.transform = "";
          const positionInViewport = ScrollTrigger.positionInViewport(
            target,
            "top"
          );
          const fromViewportTop = viewportHeight * positionInViewport;
          const fromPageTop = fromViewportTop + scrollTrigger.scroll();
          if (fromPageTop < viewportHeight) {
            endOffset = (fromViewportTop + targetHeight) * (1 / speed - 1);
            startOffset = 0;
            return;
          }
        }
        endOffset = (viewportHeight / 2 + targetHeight / 2) * (1 / speed - 1);
        startOffset = endOffset * -1;
      };
      gsap.fromTo(
        target,
        { ...dataFrom, y: () => startOffset },
        {
          ...dataTo,
          y: () => endOffset,
          ease: "none",
          scrollTrigger: {
            id: speed.toString(),
            trigger: target,
            start: () => {
              if (startOffset === 0)
                return "0";
              return `top+=${startOffset}px bottom`;
            },
            end: () => `bottom+=${endOffset}px top`,
            scrub: true,
            invalidateOnRefresh: true,
            refreshPriority: -999,
            markers,
            onRefreshInit: (self) => {
              updateOffsets(self);
            },
            onEnter: () => {
              gsap.set(target, { y: startOffset });
              if (speed > 1) {
                gsap.set(target, { visibility: "initial" });
              }
            },
            onLeave: () => {
              gsap.set(target, { y: endOffset });
            },
            onLeaveBack: () => {
              gsap.set(target, { y: startOffset });
            },
            onEnterBack: () => {
              gsap.set(target, { y: endOffset });
            },
            onToggle: ({ isActive }) => {
              gsap.set(target, {
                willChange: isActive ? "transform" : "auto"
              });
            }
          }
        }
      );
      if (speed > 1 && startOffset !== 0 && ScrollTrigger.positionInViewport(target, "bottom") > 1) {
        gsap.set(target, {
          visibility: "hidden"
        });
      }
    });
  }
}
function useDataScroll(options) {
  const autoStart = options?.autoStart ?? true;
  const selector = options?.selector ?? "[data-scroll-speed],[data-scroll-from],[data-scroll-to]";
  matchMedia = gsap.matchMedia();
  if (autoStart) {
    const targets = document.querySelectorAll(selector);
    for (const target of targets) {
      apply(target, options);
    }
  }
  return {
    apply: (target) => apply(target, options),
    destroy: () => matchMedia.revert()
  };
}

export { useDataScroll };
